import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  usePlotArea,
  useXAxisTicks,
  useYAxisTicks,
  XAxis,
  YAxis,
} from 'recharts';
import {
  DATA_ERROR_MESSAGE,
  DATA_UNAVAILABLE_MESSAGE,
} from '@data/components/basic/Table/types';
import {
  getFillForIndex,
  PatternDefs,
  TierSwatch,
} from './proficiencyPatterns';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PivotedRow {
  assessmentPeriod: string;
  [proficiency: string]: string | number;
}

const AXIS_LABEL_COLOR = 'var(--colors-neutral-700)';

// ── Y-axis ticks ───────────────────────────────────────────────────────────────

/**
 * Computes a "nice" set of y-axis ticks from 0 to >= dataMax. Targets ~5 ticks
 * with a step of 1, 2, 5, or 10 × a power of 10 so labels and grid lines line
 * up cleanly for any magnitude (e.g. 9, 47, 300, 3200).
 */
function computeYTicks(dataMax: number): number[] {
  if (dataMax <= 0) return [0, 1];
  const targetTickCount = 5;
  const rawStep = dataMax / (targetTickCount - 1);
  const pow = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / pow;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1;
  else if (normalized <= 2) niceStep = 2;
  else if (normalized <= 5) niceStep = 5;
  else niceStep = 10;
  const step = niceStep * pow;
  const top = Math.ceil(dataMax / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return ticks;
}

function AssessmentPeriodDividers() {
  const plotArea = usePlotArea();
  const xTicks = useXAxisTicks();

  if (!plotArea || !xTicks || xTicks.length < 2) return null;

  return (
    <g aria-hidden="true">
      {xTicks.slice(0, -1).map((tick, index) => {
        const nextTick = xTicks[index + 1];
        if (!nextTick) return null;

        const x = (tick.coordinate + nextTick.coordinate) / 2;

        return (
          <line
            key={`${tick.value}-${nextTick.value}`}
            x1={x}
            x2={x}
            y1={plotArea.y}
            y2={plotArea.y + plotArea.height}
            stroke="#CCCCCC"
            strokeDasharray="3 3"
          />
        );
      })}
    </g>
  );
}

// ── Tooltip / Cursor ───────────────────────────────────────────────────────────

interface GraphTooltipPayloadEntry {
  name: string;
  value: number;
  dataKey: string;
}

interface GraphTooltipProps {
  active?: boolean;
  label?: string;
  payload?: GraphTooltipPayloadEntry[];
  presentAssessmentPeriods?: Set<string>;
  proficiencyOrder?: string[];
}

/**
 * Custom Recharts tooltip content. Renders a card with a title (the hovered
 * x-axis label), one row per series, and a swatch on the left matching the
 * bar's fill (uses the same `PatternDefs` + `PROFICIENCY_PALETTE`).
 *
 * If the hovered assessment period is not present in the dataset, render
 * tooltip with "Data not yet available."
 * Note that this is different than when nulls or 0s are present.
 */
function GraphTooltip({
  active,
  label,
  payload,
  presentAssessmentPeriods,
  proficiencyOrder,
}: GraphTooltipProps) {
  if (!active || !payload?.length) return null;
  const dataAvailable = !!label && presentAssessmentPeriods?.has(label);

  return (
    <div className="-translate-x-1/2 rounded-md border border-neutral-400 bg-white p-3 shadow-md">
      <div className="mb-2 text-sm font-semibold text-black">{label}</div>
      {dataAvailable ? (
        <div className="flex flex-col gap-1">
          {payload.map((entry) => {
            const value = Number(entry.value) || 0;
            const tierIndex = proficiencyOrder?.indexOf(entry.name) ?? -1;
            return (
              <div
                key={entry.dataKey}
                className="flex items-center gap-2 text-xs font-normal text-neutral-700"
              >
                <TierSwatch index={tierIndex} />
                <span>
                  {entry.name}: {value} {value === 1 ? 'student' : 'students'}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <span className="text-xs italic text-neutral-500">
          {DATA_UNAVAILABLE_MESSAGE}
        </span>
      )}
    </div>
  );
}

interface GraphCursorProps {
  // Recharts-injected props for the cursor's bounding box
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/**
 * Custom Recharts cursor. Draws a light-grey rect over the hovered band and
 * dashed horizontal lines aligned with the y-axis ticks (read from the chart
 * context via `useYAxisTicks`) so the cursor visually extends the existing
 * chart grid.
 */
function GraphCursor({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
}: GraphCursorProps) {
  const yTicks = useYAxisTicks();
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill="#F5F5F5" />
      {yTicks?.map((tick) => (
        <line
          key={tick.value as string | number}
          x1={x}
          x2={x + width}
          y1={tick.coordinate}
          y2={tick.coordinate}
          stroke="#CCCCCC"
          strokeDasharray="3 3"
        />
      ))}
    </g>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

interface ReportProficiencyGraphProps {
  error: Error | null;
  loading: boolean;
  label: string | null | undefined;
  description: string | null | undefined;
  rows: PivotedRow[];
  proficiencyOrder: string[];
  assessmentPeriodMonths?: Record<string, string>;
  presentAssessmentPeriods?: Set<string>;
  axisLabels?: { x: string; y: string };
}

interface AssessmentPeriodAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string };
  subtitles?: Record<string, string>;
}

function AssessmentPeriodAxisTick({
  x = 0,
  y = 0,
  payload,
  subtitles,
}: AssessmentPeriodAxisTickProps) {
  const value = payload?.value ?? '';
  const subtitle = subtitles?.[value];
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" dy={12} fontSize={12} fill={AXIS_LABEL_COLOR}>
        <tspan x={0}>{value}</tspan>
        {subtitle && (
          <tspan x={0} dy={14}>
            {subtitle}
          </tspan>
        )}
      </text>
    </g>
  );
}

export function ReportProficiencyGraph({
  error,
  loading,
  label,
  description,
  rows,
  proficiencyOrder,
  assessmentPeriodMonths,
  presentAssessmentPeriods,
  axisLabels,
}: ReportProficiencyGraphProps) {
  const yTicks = useMemo(() => {
    let max = 0;
    rows.forEach((row) => {
      proficiencyOrder.forEach((p) => {
        const v = Number(row[p]) || 0;
        if (v > max) max = v;
      });
    });
    return computeYTicks(max);
  }, [rows, proficiencyOrder]);

  if (loading) {
    return (
      <div className="flex min-w-0 flex-col gap-4 rounded-lg border border-neutral-400 p-4">
        <LoadingBar announce className="ml-0 h-8 max-w-[200px]" />
        <LoadingBar announce className="mx-0 h-[300px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-w-0 flex-col rounded-lg border border-neutral-400 p-4">
        <div className="flex h-[155px] items-center justify-center rounded border border-dashed border-neutral-400 px-4 py-6">
          <span className="text-sm text-neutral-700">{DATA_ERROR_MESSAGE}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-neutral-400 p-4">
      <span className="text-xl font-semibold">{label}</span>
      <span className="mb-4 text-sm text-neutral-900">{description}</span>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold">Proficiency Level</span>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {proficiencyOrder.map((proficiency, index) => (
            <div
              key={proficiency}
              className="flex items-center gap-2 text-xs text-neutral-700"
            >
              <TierSwatch
                index={index}
                ariaLabel={`${proficiency} proficiency pattern`}
              />
              <span>{proficiency}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 w-full overflow-hidden">
        <div className="w-full overflow-x-auto">
          <div className="h-80 w-full min-w-[500px] overflow-hidden">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minHeight={0}
              minWidth={0}
              initialDimension={{ width: 500, height: 320 }}
            >
              <BarChart
                data={rows}
                margin={{ top: 8, right: 16, left: 24, bottom: 40 }}
                aria-label={`${label ?? 'Student Proficiency'} bar chart`}
              >
                {rows
                  .filter(
                    (r) => !presentAssessmentPeriods?.has(r.assessmentPeriod)
                  )
                  .map((r) => (
                    <ReferenceArea
                      key={r.assessmentPeriod}
                      x1={r.assessmentPeriod}
                      x2={r.assessmentPeriod}
                      fill="#F5F5F5"
                      fillOpacity={0.5}
                      label={{
                        value: DATA_UNAVAILABLE_MESSAGE,
                        position: 'center',
                        fill: '#6D6D6D',
                        fontSize: 12,
                        fontWeight: 600,
                        stroke: '#FFFFFF',
                        strokeWidth: 4,
                        paintOrder: 'stroke',
                      }}
                    />
                  ))}
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <AssessmentPeriodDividers />
                <XAxis
                  dataKey="assessmentPeriod"
                  stroke="var(--colors-neutral-200)"
                  tick={
                    <AssessmentPeriodAxisTick
                      subtitles={assessmentPeriodMonths}
                    />
                  }
                  tickLine={false}
                  interval={0}
                  label={{
                    value: axisLabels?.x ?? '',
                    position: 'insideBottom',
                    offset: -32,
                    fill: AXIS_LABEL_COLOR,
                    style: { fontSize: 12, fontWeight: 600 },
                  }}
                />
                <YAxis
                  allowDecimals={false}
                  stroke="var(--colors-neutral-200)"
                  domain={[0, yTicks[yTicks.length - 1] ?? 0]}
                  ticks={yTicks}
                  tick={{ fontSize: 12, fill: AXIS_LABEL_COLOR }}
                  tickLine={false}
                  label={{
                    value: axisLabels?.y ?? '',
                    angle: -90,
                    position: 'insideLeft',
                    fill: AXIS_LABEL_COLOR,
                    stroke: 'none',
                    style: {
                      textAnchor: 'middle',
                      fontSize: 12,
                      fontWeight: 600,
                    },
                  }}
                />
                <Tooltip
                  content={
                    <GraphTooltip
                      presentAssessmentPeriods={presentAssessmentPeriods}
                      proficiencyOrder={proficiencyOrder}
                    />
                  }
                  cursor={<GraphCursor />}
                  allowEscapeViewBox={{ x: true }}
                />

                <PatternDefs />

                {proficiencyOrder.map((proficiency, index) => (
                  <Bar
                    key={proficiency}
                    dataKey={proficiency}
                    fill={getFillForIndex(index)}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
